import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Stack,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  LinearProgress,
  Badge,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MoneyIcon from '@mui/icons-material/AttachMoney';
import DealIcon from '@mui/icons-material/Handshake';
import InventoryIcon from '@mui/icons-material/Inventory';
import SpeedIcon from '@mui/icons-material/Speed';
import FarmIcon from '@mui/icons-material/Agriculture';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AIIcon from '@mui/icons-material/AutoAwesome';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useAuth } from '../context/AuthContext';
import { useShop } from '../context/ShopContext';
import { useDashboardNotifications } from '../context/DashboardNotificationContext';

/**
 * Elite Dashboard Command Center
 * Serves as the primary landing page for authenticated users.
 * Summarizes wealth, deals, inventory, and AI insights.
 */
const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { activeShop } = useShop();
  const navigate = useNavigate();
  const { unreadCount } = useDashboardNotifications();

  // Mock Stats for Command Center
  const stats = [
    { title: 'Monthly Revenue', value: 'KSh 142,500', trend: '+12.5%', icon: <MoneyIcon color="primary" />, color: 'primary' },
    { title: 'Active B2B Deals', value: '4', trend: '2 Pending', icon: <DealIcon color="secondary" />, color: 'secondary' },
    { title: 'Inventory Status', value: '92%', trend: 'Optimal', icon: <InventoryIcon color="success" />, color: 'success' },
    { title: 'Farmer Credit Score', value: '720', trend: 'Good', icon: <SpeedIcon color="warning" />, color: 'warning' },
  ];

  return (
    <Box>
      {/* Welcome Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Command Center</Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back, {user?.name || 'Farmer'}! Here's your agricultural empire at a glance.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button 
            variant="contained" 
            startIcon={<FarmIcon />} 
            onClick={() => navigate('/dashboard/my-farms')}
            sx={{ borderRadius: '12px', px: 3 }}
          >
            Manage Farms
          </Button>
          <IconButton 
            onClick={() => navigate('/dashboard/notifications')}
            sx={{ 
              bgcolor: 'background.paper', 
              boxShadow: 1,
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon color="action" />
            </Badge>
          </IconButton>
        </Stack>
      </Box>

      {/* Primary Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider', transition: 'all 0.2s', '&:hover': { boxShadow: 3, transform: 'translateY(-4px)' } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Box sx={{ bgcolor: `${stat.color}.light`, p: 1, borderRadius: '8px', display: 'flex', opacity: 0.8 }}>
                  {stat.icon}
                </Box>
                <Chip label={stat.trend} size="small" color={stat.trend.includes('+') ? 'success' : 'default'} sx={{ fontWeight: 'bold', fontSize: '10px' }} />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 'bold' }}>
                {stat.title}
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5 }}>{stat.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Active B2B Negotiations Summary */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: '16px', height: '100%', border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6" fontWeight="bold">Active Negotiations</Typography>
              <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate(`/dashboard/my-shops/${activeShop?.id}/deals`)}>
                View Deal Room
              </Button>
            </Stack>
            <Stack spacing={2}>
              {[
                { buyer: 'Grand Nairobi Hotel', item: 'Organic Tomatoes', value: 'KSh 42,500', status: 'Negotiating' },
                { buyer: 'Fresh Markets Ltd', item: 'Hybrid Maize', value: 'KSh 120,000', status: 'Pending Approval' },
                { buyer: 'Zuri Exports', item: 'Avocados', value: 'KSh 85,000', status: 'Finalized' },
              ].map((deal, idx) => (
                <Box key={idx} sx={{ p: 2, borderRadius: '12px', border: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'background-color 0.2s', '&:hover': { bgcolor: 'grey.50' } }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">{deal.buyer}</Typography>
                    <Typography variant="caption" color="text.secondary">{deal.item}</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="subtitle2" fontWeight="bold">{deal.value}</Typography>
                    <Chip 
                      label={deal.status} 
                      size="small" 
                      color={deal.status === 'Finalized' ? 'success' : 'primary'} 
                      variant="outlined" 
                      sx={{ height: 20, fontSize: '10px', fontWeight: 'bold' }} 
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* AI Intelligence & Alerts */}
        <Grid item xs={12} md={4}>
          <Paper 
            sx={{ 
              p: 3, 
              borderRadius: '16px', 
              bgcolor: '#1a237e', // Explicit deep blue for "AI" premium feel
              color: 'white', 
              boxShadow: 4,
              height: '100%'
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <AIIcon sx={{ color: '#ffd700' }} />
              <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>AI Insights</Typography>
              <Chip label="PRO" size="small" sx={{ height: 18, fontSize: '10px', fontWeight: 'bold', bgcolor: '#ffd700', color: '#1a237e' }} />
            </Stack>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ color: 'rgba(255,255,255,0.9)' }}>Market Sentiment</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={78} 
                    sx={{ 
                      flexGrow: 1, 
                      height: 8, 
                      borderRadius: 5, 
                      bgcolor: 'rgba(255,255,255,0.1)', 
                      '& .MuiLinearProgress-bar': { bgcolor: '#ffd700' } 
                    }} 
                  />
                  <Typography variant="caption" fontWeight="bold" sx={{ color: 'white' }}>78%</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mt: 0.5 }}>
                  Demand for organic produce is rising in your region.
                </Typography>
              </Box>
              
              <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
                <Typography variant="subtitle2" fontWeight="bold" color="warning.main">Stock Alert</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>
                  "Hybrid Maize Seeds" will be out of stock in 3 days. Restock now to capture KSh 12,000 in revenue.
                </Typography>
              </Box>

              <Button 
                variant="contained" 
                fullWidth 
                sx={{ 
                  bgcolor: '#ffd700', 
                  color: '#1a237e', 
                  '&:hover': { bgcolor: '#ffea00' }, 
                  fontWeight: 'bold',
                  borderRadius: '12px'
                }}
              >
                Run Full Analysis
              </Button>
            </Stack>
          </Paper>
        </Grid>

        {/* Market Price Trends Summary */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Real-Time Market Intelligence</Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {[
                { crop: 'Tomatoes', price: 'KSh 85/kg', trend: '+5%', county: 'Nairobi' },
                { crop: 'Maize', price: 'KSh 3,200/bag', trend: '-2%', county: 'Kiambu' },
                { crop: 'Onions', price: 'KSh 110/kg', trend: '+12%', county: 'Mombasa' },
                { crop: 'Potatoes', price: 'KSh 2,800/bag', trend: '+1%', county: 'Nakuru' },
              ].map((item, idx) => (
                <Grid item xs={6} md={3} key={idx}>
                  <Box sx={{ p: 2, textAlign: 'center', borderRight: { md: idx < 3 ? '1px solid' : 'none' }, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.primary" sx={{ fontWeight: 'bold', opacity: 0.8 }}>{item.county}</Typography>
                    <Typography variant="subtitle1" fontWeight="bold">{item.crop}</Typography>
                    <Typography variant="h6" color="primary.main" fontWeight="bold">{item.price}</Typography>
                    <Typography variant="caption" color={item.trend.includes('+') ? 'success.main' : 'error.main'} sx={{ fontWeight: 'bold' }}>
                      {item.trend} this week
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
